import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { VesselRequest, VesselResponse, VesselsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface Vessel extends VesselResponse { }

export interface State {
  isLoading: boolean;
  error: boolean;
  vesselList: Vessel[];
  selectedVessel?: Vessel;
}

const initialState: State = {
  isLoading: false,
  error: false,
  vesselList: [],
};

const vesselSlice = createSlice({
  name: 'vessel',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getVesselListSuccess: (state, action) => {
      state.vesselList = action.payload;
      state.isLoading = false;
    },
    getVesselSuccess: (state, action: PayloadAction<Vessel | undefined>) => {
      state.selectedVessel = action.payload;
      state.isLoading = false;
    },
    addNewVesselSuccess: (state, action: PayloadAction<Vessel>) => {
      const newVessel = action.payload;
      state.vesselList = [newVessel, ...state.vesselList];
      state.isLoading = false;
    },
    updateVesselSuccess: (state, action: PayloadAction<Vessel>) => {
      const updatedItem = action.payload;
      const index = state.vesselList.findIndex(item => item.vesselId === updatedItem.vesselId);
      state.vesselList = [...state.vesselList.slice(0, index), updatedItem, ...state.vesselList.slice(index + 1)];
      state.isLoading = false;
    },
  },
});

// Reducer
export default vesselSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
  getVesselListSuccess,
  getVesselSuccess,
  addNewVesselSuccess,
  updateVesselSuccess,
} = vesselSlice.actions;

// for useSelector
export const selectVessel = (state: RootState) => state.vessel as State;

// ----------------------------------------------------------------------
export function getAllVessels(ignoreCache: boolean = false) {
  return async (dispatch, getState) => {
    try {
      const { vesselList } = selectVessel(getState());
      if (ignoreCache || vesselList.length === 0) {
        dispatch(vesselSlice.actions.startLoading());
        const client = new VesselsClient(undefined, axios);
        const response = await client.getAll(false);
        dispatch(vesselSlice.actions.getVesselListSuccess(response));
      }
    } catch (error) {
      dispatch(vesselSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function getVessel(id: number) {
  return async (dispatch, getState) => {
    if (id > 0) {
      dispatch(vesselSlice.actions.startLoading());
      try {
        const client = new VesselsClient(undefined, axios);
        const response = await client.get(id);
        dispatch(vesselSlice.actions.getVesselSuccess(response));
      } catch (error) {
        dispatch(vesselSlice.actions.hasError(true));
        handleError(error);
      }
    } else {
      dispatch(vesselSlice.actions.getVesselSuccess(undefined));
    }
  };
}

export function createVessel(vessel: Vessel, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(vesselSlice.actions.startLoading());
    try {
      const { vesselId, ...payload } = vessel;
      const request = payload as VesselRequest;
      const client = new VesselsClient(undefined, axios);
      const response = await client.create(request);
      dispatch(vesselSlice.actions.addNewVesselSuccess(response));
      if (showNotification) {
        toast.success(`${response.vesselName} created successfully!`);
      }
    } catch (error) {
      dispatch(vesselSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function updateVessel(vessel: Vessel, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(vesselSlice.actions.startLoading());
    try {
      const { vesselId, ...payload } = vessel;
      const request = payload as VesselRequest;
      const client = new VesselsClient(undefined, axios);
      const response = await client.edit(vesselId, request);
      dispatch(vesselSlice.actions.updateVesselSuccess(response));
      if (showNotification) {
        toast.success(`${response.vesselName} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
      }
    } catch (error) {
      dispatch(vesselSlice.actions.hasError(true));
      handleError(error);
    }
  };
}